/**
 * The SMS messaging panel — a single iframe pointed at Surge's own
 * embeddable Inbox or Conversation component (embed.surge.app). Surge's
 * embed is the actual compose/send UI; this component's only job is
 * fetching a short-lived embed token and building the right URL.
 *
 * Same dropdown mechanics as SettingsPanel/StaffLayout's NavGroup:
 * outside-click and Escape both close it. Anchored under `MessagingPill`,
 * which is itself absolutely centered in `.shell__header` — see
 * styles.css's `.messaging-pill`/`.messaging-panel`.
 */

import { useEffect, useRef, useState } from 'react';
import { X } from 'lucide-react';
import { api } from '../lib/api.js';
import { useMessaging } from '../lib/messaging-context.js';

const SURGE_EMBED_BASE = 'https://embed.surge.app';

interface InboxEmbedResponse {
  surgeUserId: string;
  token: string;
}

interface TenantEmbedResponse extends InboxEmbedResponse {
  target:
    | { mode: 'existing'; surgeConversationId: string }
    | { mode: 'new'; phoneNumber: string; firstName: string; lastName: string };
}

function buildEmbedUrl(
  view: 'inbox' | { tenantId: string },
  data: InboxEmbedResponse | TenantEmbedResponse,
): string {
  const params = new URLSearchParams({ user_id: data.surgeUserId, token: data.token });

  if (view === 'inbox') {
    return `${SURGE_EMBED_BASE}/conversations?${params.toString()}`;
  }

  const target = (data as TenantEmbedResponse).target;
  if (target.mode === 'existing') {
    return `${SURGE_EMBED_BASE}/conversation/${target.surgeConversationId}?${params.toString()}`;
  }
  params.set('phone_number', target.phoneNumber);
  if (target.firstName) params.set('first_name', target.firstName);
  if (target.lastName) params.set('last_name', target.lastName);
  return `${SURGE_EMBED_BASE}/conversation/new?${params.toString()}`;
}

export function MessagingPanel() {
  const { state, close } = useMessaging();
  const ref = useRef<HTMLDivElement>(null);
  const [embedUrl, setEmbedUrl] = useState<string | null>(null);
  const [error, setError] = useState<unknown>(null);

  // Deliberately not `state.view !== 'closed'` — the consent-check/
  // consent-required/consent-blocked states (messaging-context.tsx) have
  // their own rendering (SmsConsentDialog / a blocked message, both in
  // StaffLayout) and must not also make this panel's shell appear.
  const open = state.view === 'inbox' || state.view === 'tenant';

  useEffect(() => {
    if (!open) return;
    function onPointerDown(event: PointerEvent) {
      if (ref.current && !ref.current.contains(event.target as Node)) close();
    }
    function onKeyDown(event: KeyboardEvent) {
      if (event.key === 'Escape') close();
    }
    document.addEventListener('pointerdown', onPointerDown);
    document.addEventListener('keydown', onKeyDown);
    return () => {
      document.removeEventListener('pointerdown', onPointerDown);
      document.removeEventListener('keydown', onKeyDown);
    };
  }, [open, close]);

  // A plain string key, not the `state` object itself — `openConversationForTenant`
  // creates a new object on every call, which would otherwise re-fetch a
  // token on every re-render rather than only when the target actually
  // changes.
  const requestKey = state.view === 'tenant' ? `tenant:${state.tenant.id}` : state.view;

  useEffect(() => {
    if (!open) {
      setEmbedUrl(null);
      return;
    }
    setEmbedUrl(null);
    setError(null);
    let cancelled = false;

    if (state.view === 'inbox') {
      void api<InboxEmbedResponse>('/messaging/inbox-embed')
        .then((data) => {
          if (!cancelled) setEmbedUrl(buildEmbedUrl('inbox', data));
        })
        .catch((caught: unknown) => {
          if (!cancelled) setError(caught);
        });
    } else if (state.view === 'tenant') {
      void api<TenantEmbedResponse>(`/messaging/tenants/${state.tenant.id}/embed`)
        .then((data) => {
          if (!cancelled) setEmbedUrl(buildEmbedUrl({ tenantId: state.tenant.id }, data));
        })
        .catch((caught: unknown) => {
          if (!cancelled) setError(caught);
        });
    }

    return () => {
      cancelled = true;
    };
  }, [open, requestKey]);

  if (!open) return null;

  const title = state.view === 'tenant' ? `Text ${state.tenant.name}` : 'Messages';

  return (
    <div className="messaging-panel card" ref={ref}>
      <div className="messaging-panel__header">
        <h2>{title}</h2>
        <button type="button" className="messaging-panel__close" onClick={close} aria-label="Close">
          <X size={16} strokeWidth={2} />
        </button>
      </div>
      <div className="messaging-panel__body">
        {error ? (
          <p className="alert alert--error">
            {error instanceof Error ? error.message : 'Could not load messaging.'}
          </p>
        ) : embedUrl ? (
          <iframe src={embedUrl} title="SMS messaging" className="messaging-panel__iframe" />
        ) : (
          <p className="muted">Loading…</p>
        )}
      </div>
    </div>
  );
}
